// Navbar.tsx
import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useLanguage, Language } from "@/lib/LanguageContext";
import {
  Menu,
  Car,
  Plane,
  Hotel,
  Compass,
  Map,
  ChevronDown,
  Facebook,
  Twitter,
  Instagram,
  Heart,
  Search,
  Image,
  Footprints,
  Mountain,
  Utensils,
  Globe,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { faXTwitter } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Helper component to initialize Google Translate in Mobile Menu
const MobileTranslate = () => {
  useEffect(() => {
    // Retry initialization in case script loads late
    let checkInterval: NodeJS.Timeout;
    let attempts = 0;

    const initTranslate = () => {
      // @ts-ignore
      if (window.google && window.google.translate && window.google.translate.TranslateElement) {
        try {
          // Clear existing if any (prevent duplicates)
          const element = document.getElementById('google_translate_element_mobile');
          if (element) element.innerHTML = '';

          // @ts-ignore
          new window.google.translate.TranslateElement(
            {
              pageLanguage: 'en',
              includedLanguages: 'en,fr,es,de,it,sw,da,sv,vi,hi',
              // @ts-ignore
              layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE
            },
            'google_translate_element_mobile'
          );
          if (checkInterval) clearInterval(checkInterval);
        } catch (e) {
          console.error("Google Translate Init Error:", e);
        }
      } else {
        attempts++;
        if (attempts >= 20) { // Stop after 10 seconds
          if (checkInterval) clearInterval(checkInterval);
        }
      }
    };

    // Run immediately then poll
    initTranslate();
    checkInterval = setInterval(initTranslate, 500);

    return () => {
      if (checkInterval) clearInterval(checkInterval);
    }
  }, []);

  return <div id="google_translate_element_mobile" className="w-full"></div>;
};

interface NavItem {
  name: string;
  href: string;
  key?: string;
}

const countries = [
  {
    name: "Uganda",
    href: "/destinations#uganda",
    description:
      "Experience the Pearl of Africa's stunning wildlife and landscapes",
    image: "https://images.unsplash.com/photo-1594068337983-b24ff1207b1b",
    tours: [
      {
        name: "3-Day Murchison Falls Adventure Safari",
        href: "/tours/uganda/murchison-falls-3-day",
      },
      {
        name: "3-Day Queen Elizabeth National Park Safari",
        href: "/tours/uganda/queen-elizabeth-3-day",
      },
      {
        name: "5-Day Bwindi Impenetrable National Park Gorilla Safari",
        href: "/tours/uganda/bwindi-gorilla-5-day",
      },
      {
        name: "5-Day Kidepo Valley National Park Safari",
        href: "/tours/uganda/kidepo-valley-5-day",
      },
      {
        name: "5-Day Uganda Wildlife Safari",
        href: "/tours/uganda/wildlife-5-day",
      },
      {
        name: "8-Day Uganda Adventure Safari",
        href: "/tours/uganda/adventure-8-day",
      },
      {
        name: "8-Day Primates and Wildlife Safaris",
        href: "/tours/uganda/primates-wildlife-8-day",
      },
      {
        name: "19-Day Uganda Experience Nature",
        href: "/tours/uganda/experience-nature-19-day",
      },
      {
        name: "21-Day Uganda Cultural and Wildlife Safari",
        href: "/tours/uganda/cultural-wildlife-21-day",
      },
    ],
  },
  {
    name: "Rwanda",
    href: "/destinations#rwanda",
    description: "Discover the land of a thousand hills and mountain gorillas",
    image: "https://images.unsplash.com/photo-1559628233-100c798642d4",
    tours: [
      {
        name: "7-Day Complete Rwanda Tour",
        href: "/tours/rwanda/complete-7-day",
      },
      {
        name: "4 Days Double Gorilla Trekking in Rwanda",
        href: "/tours/rwanda/double-gorilla-4-day",
      },
      {
        name: "4 Days Nyungwe Forest National Park and Lake Kivu Tour",
        href: "/tours/rwanda/nyungwe-kivu-4-day",
      },
      {
        name: "3 Days Rwanda Gorilla and Golden Monkey Trekking Safari",
        href: "/tours/rwanda/gorilla-monkey-3-day",
      },
      {
        name: "2 Days Nyungwe Chimpanzee Trekking Rwanda Safari",
        href: "/tours/rwanda/nyungwe-chimp-2-day",
      },
      {
        name: "7 Days Volcanoes National Park Adventure",
        href: "/tours/rwanda/volcanoes-7-day",
      },
      {
        name: "10 Days Rwanda Safari",
        href: "/tours/rwanda/safari-10-day",
      },
      {
        name: "2 Days Rwanda Gorilla Safari",
        href: "/tours/rwanda/gorilla-2-day",
      },
      {
        name: "5 Days Rwanda Primates Safari",
        href: "/tours/rwanda/primates-5-day",
      },
      {
        name: "4 Days Akagera National Park Safari",
        href: "/tours/rwanda/akagera-4-day",
      },
    ],
  },
  {
    name: "Tanzania",
    href: "/destinations#tanzania",
    description: "Visit the Serengeti and climb Mt. Kilimanjaro",
    image: "https://images.unsplash.com/photo-1516026672322-bc52d61a55d5",
    tours: [
      {
        name: "2 Days Safari to Tanzania",
        href: "/tours/tanzania/safari-2-day",
      },
      {
        name: "6 Days Safari to Tanzania",
        href: "/tours/tanzania/safari-6-day",
      },
      {
        name: "8 Days Wildlife Tanzania Safari",
        href: "/tours/tanzania/wildlife-8-day",
      },
    ],
  },
  {
    name: "Kenya",
    href: "/destinations#kenya",
    description: "Explore the home of the great migration and Masai culture",
    image: "https://images.unsplash.com/photo-1547471080-7cc2caa01a7e",
    tours: [
      {
        name: "3 Days Maasai Mara National Reserve Safari",
        href: "/tours/kenya/maasai-mara-3-day",
      },
      {
        name: "3 Days Masai Mara Road Safari",
        href: "/tours/kenya/masai-mara-road-3-day",
      },
      {
        name: "4 Days Masai Mara Road Safari",
        href: "/tours/kenya/masai-mara-road-4-day",
      },
      {
        name: "5 Days Lake Nakuru National Park, Masai Mara National Reserve",
        href: "/tours/kenya/nakuru-maasai-mara-5-day",
      },
      {
        name: "7 Days Magical Kenya Family Safari",
        href: "/tours/kenya/family-7-day",
      },
      {
        name: "9 Days Magical Kenya Safari",
        href: "/tours/kenya/magical-9-day",
      },
    ],
  },
];

const services = [
  {
    name: "Car Hire",
    href: "/services/car-hire",
    description: "4x4 vehicles perfect for safari adventures",
    icon: Car,
    features: [
      "Latest model 4x4 vehicles",
      "Professional drivers available",
      "Comprehensive insurance coverage",
      "Custom itinerary planning",
    ],
  },
  {
    name: "Air Ticketing",
    href: "/services/air-ticketing",
    description: "Domestic and international flight bookings",
    icon: Plane,
    features: [
      "Best deals on flights",
      "Multiple airline options",
      "Group booking discounts",
      "24/7 travel support",
    ],
  },
  {
    name: "Accommodation",
    href: "/services/accommodation",
    description: "Luxury lodges to tented camps",
    icon: Hotel,
    features: [
      "Luxury safari lodges",
      "Tented wilderness camps",
      "Boutique hotels",
      "All-inclusive packages",
    ],
  },
  {
    name: "Wildlife & Birding Safaris",
    href: "/services/wildlife-birding-safaris",
    description: "Guided wildlife and bird watching experiences",
    icon: Compass,
    features: [
      "Expert wildlife guides",
      "Bird watching specialists",
      "Photography tours",
      "Conservation focused",
    ],
  },
  {
    name: "Gorilla & Primate Trekking",
    href: "/services/gorilla-primate-trekking",
    description: "Expert-guided gorilla and primate adventures",
    icon: Footprints,
    features: [
      "17,000 km² of gorilla habitat",
      "Over 5,000 chimpanzees",
      "13 primate species",
      "Expert trekking guides",
    ],
  },
  {
    name: "Soft & Extreme Adventures",
    href: "/services/soft-extreme-adventures",
    description: "Thrilling adventures across East Africa",
    icon: Mountain,
    features: [
      "Kayaking and canoeing",
      "Whitewater rafting",
      "Mountain hiking",
      "Rock climbing",
    ],
  },
  {
    name: "Cultural & Culinary Tours",
    href: "/services/cultural-culinary-tours",
    description: "Immersive cultural experiences",
    icon: Utensils,
    features: [
      "Traditional dances",
      "Local cuisine tasting",
      "Village visits",
      "Cultural workshops",
    ],
  },
  {
    name: "Airport Transport",
    href: "/services/airport-transport",
    description: "Reliable airport transfer services",
    icon: Plane,
    features: [
      "24/7 availability",
      "Professional drivers",
      "Modern fleet",
      "Flight monitoring",
    ],
  },
];

const languages = [
  { name: "English", code: "en", flag: "🇬🇧" },
  { name: "French", code: "fr", flag: "🇫🇷" },
  { name: "Spanish", code: "es", flag: "🇪🇸" },
  { name: "German", code: "de", flag: "🇩🇪" },
  { name: "Italian", code: "it", flag: "🇮🇹" },
  { name: "Dutch", code: "nl", flag: "🇳🇱" },
  { name: "Portuguese", code: "pt", flag: "🇵🇹" },
  { name: "Chinese", code: "zh-CN", flag: "🇨🇳" },
  { name: "Japanese", code: "ja", flag: "🇯🇵" },
  { name: "Arabic", code: "ar", flag: "🇦🇪" },
  { name: "Russian", code: "ru", flag: "🇷🇺" },
  { name: "Swahili", code: "sw", flag: "🇹🇿" },
  { name: "Danish", code: "da", flag: "🇩🇰" },
  { name: "Swedish", code: "sv", flag: "🇸🇪" },
  { name: "Vietnamese", code: "vi", flag: "🇻🇳" },
  { name: "Hindi", code: "hi", flag: "🇮🇳" },
];

const navigation = [
  { name: "Ecocentric", href: "/ecocentric", key: "ecocentric" },
  { name: "Blog", href: "/blog", key: "blog" },
  { name: "Booking", href: "/booking", key: "booking" },
];

export default function Navbar() {
  const [location] = useLocation();
  const [open, setOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [activeCountry, setActiveCountry] = useState(countries[0]);
  const [activeService, setActiveService] = useState(services[0]);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileDestinationsOpen, setMobileDestinationsOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileLangMenuOpen, setMobileLangMenuOpen] = useState(false);
  const timeoutRef = useRef<number>();
  const servicesTimeoutRef = useRef<number>();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Use the language context instead of local state
  const { currentLanguage, setLanguage, t } = useLanguage();

  // Find the current language object from the code
  const currentLangObj =
    languages.find((lang) => lang.code === currentLanguage) || languages[0];

  const allTours = countries.flatMap((country) =>
    country.tours.map((tour) => ({
      ...tour,
      country: country.name,
    })),
  );

  const filteredTours = allTours.filter(
    (tour) =>
      tour.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tour.country.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = window.setTimeout(() => {
      setDropdownOpen(false);
    }, 300);
  };

  const handleServicesMouseEnter = () => {
    if (servicesTimeoutRef.current) {
      window.clearTimeout(servicesTimeoutRef.current);
    }
    setServicesDropdownOpen(true);
  };

  const handleServicesMouseLeave = () => {
    servicesTimeoutRef.current = window.setTimeout(() => {
      setServicesDropdownOpen(false);
    }, 300);
  };

  useEffect(() => {
    // Handle scroll event for navbar
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
      if (servicesTimeoutRef.current) {
        window.clearTimeout(servicesTimeoutRef.current);
      }
    };
  }, []);

  return (
    <nav
      className={`sticky top-0 z-50 transition-all duration-300  ${isScrolled
        ? "bg-black/95 backdrop-blur supports-[backdrop-filter]:bg-black/80"
        : "bg-black"
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-20">
          <div className="flex-shrink-0">
            <Link href="/" className="flex items-center">
              <img
                src="/ecoplacetourslogo.png"
                alt="Ecoplace Tours"
                className="h-20 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Move the mobile menu to the right */}
          <div className="md:hidden ml-auto flex items-center space-x-1">
            <div id="google_translate_element_mobile" className="hidden" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white text-xl p-2 touch-manipulation">
                  {currentLangObj.flag}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[150px] max-h-[300px] overflow-y-auto bg-white z-[100]">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);

                      // Google Translate treats the base language (English) differently.
                      // To revert, we must clear its cookies and reload the page.
                      if (lang.code === 'en') {
                        const domain = window.location.hostname;
                        document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
                        document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=${domain}; path=/;`;
                        document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=.${domain}; path=/;`;
                        window.location.reload();
                        return;
                      }

                      const selectId = '.goog-te-combo';
                      const selects = document.querySelectorAll(selectId);
                      selects.forEach(select => {
                        (select as HTMLSelectElement).value = lang.code;
                        select.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
                      });

                      // Fallback just in case Google Translate doesn't pick up the event
                      document.cookie = `googtrans=/en/${lang.code}; path=/`;
                      document.cookie = `googtrans=/en/${lang.code}; domain=.${window.location.hostname}; path=/`;
                      if (!document.querySelector('.goog-te-banner-frame')) {
                        window.location.reload();
                      }
                    }}
                    className="cursor-pointer flex items-center gap-3 py-2"
                  >
                    <span className="text-xl">{lang.flag}</span>
                    <span className="font-medium">{lang.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white touch-manipulation p-2"
                >
                  <Menu className="h-8 w-8" />
                </Button>
              </SheetTrigger>
              <SheetContent
                onOpenAutoFocus={(e) => e.preventDefault()}
                className="bg-gradient-to-b from-black to-gray-900 text-white w-full sm:w-80 overflow-y-auto max-h-[100dvh] border-l border-gray-800"
              >
                <div className="flex justify-center items-center py-8">
                  <img
                    src="/ecoplace-logo.png"
                    alt="Ecoplace Tours"
                    className="h-20 w-auto object-contain"
                  />
                </div>

                <div className="flex flex-col space-y-1 pb-20">
                  <div className="py-2">
                    <div className="px-4 mb-4">
                      <Input
                        type="search"
                        placeholder="Search tours..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-400"
                      />
                    </div>
                    {searchQuery && filteredTours.length > 0 && (
                      <div className="px-4 space-y-1">
                        {filteredTours.map((tour) => (
                          <Link
                            key={tour.href}
                            href={tour.href}
                            onClick={() => {
                              setOpen(false);
                              setSearchQuery("");
                            }}
                            className="block text-sm text-gray-300 hover:text-white py-2 px-3 rounded-md transition-colors hover:bg-gray-800/50"
                          >
                            <span className="flex items-center">
                              <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2 flex-shrink-0" />
                              {tour.name}
                              <span className="text-gray-500 ml-2">
                                ({tour.country})
                              </span>
                            </span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>

                  <Link
                    href="/"
                    onClick={() => setOpen(false)}
                    className={`${location === "/"
                      ? "text-primary font-semibold bg-primary/10"
                      : "text-gray-300"
                      } px-4 py-3 text-lg font-medium hover:bg-gray-800/50 rounded-lg transition-colors touch-manipulation`}
                  >
                    {t("home")}
                  </Link>

                  <div className="py-2">
                    <button
                      onClick={() =>
                        setMobileDestinationsOpen(!mobileDestinationsOpen)
                      }
                      className="w-full px-4 py-3 flex items-center justify-between text-lg font-medium text-gray-300 hover:bg-gray-800/50 rounded-lg transition-colors touch-manipulation"
                    >
                      <span>{t("destinations")}</span>
                      <ChevronDown
                        className={`w-5 h-5 transition-transform duration-200 ${mobileDestinationsOpen ? "rotate-180" : ""
                          }`}
                      />
                    </button>
                    {mobileDestinationsOpen && (
                      <div className="mt-1 space-y-1">
                        {countries.map((country) => (
                          <div key={country.name} className="overflow-hidden">
                            <button
                              onClick={() => setActiveCountry(country)}
                              className="w-full px-6 py-2 flex items-center justify-between text-base font-medium text-gray-300 hover:bg-gray-800/50 transition-colors touch-manipulation rounded-lg"
                            >
                              <span>{country.name}</span>
                              <ChevronDown
                                className={`w-4 h-4 transition-transform duration-200 ${activeCountry === country ? "rotate-180" : ""
                                  }`}
                              />
                            </button>
                            {activeCountry === country && (
                              <div className="pl-8 pr-4 py-2 space-y-1 bg-gray-800/20 rounded-lg mx-2 mt-1">
                                {country.tours.map((tour) => (
                                  <Link
                                    key={tour.href}
                                    href={tour.href}
                                    onClick={() => setOpen(false)}
                                    className="block text-sm text-gray-400 hover:text-white py-2 px-3 rounded-md transition-colors touch-manipulation"
                                  >
                                    <span className="flex items-center">
                                      <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2 flex-shrink-0" />
                                      {tour.name}
                                    </span>
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="py-2">
                    <button
                      onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                      className="w-full px-4 py-3 flex items-center justify-between text-lg font-medium text-gray-300 hover:bg-gray-800/50 rounded-lg transition-colors touch-manipulation"
                    >
                      <span>{t("services")}</span>
                      <ChevronDown
                        className={`w-5 h-5 transition-transform duration-200 ${mobileServicesOpen ? "rotate-180" : ""
                          }`}
                      />
                    </button>
                    {mobileServicesOpen && (
                      <div className="mt-1 space-y-1">
                        {services.map((service) => (
                          <Link
                            key={service.name}
                            href={service.href}
                            onClick={() => setOpen(false)}
                            className="flex items-center px-6 py-2 text-base text-gray-300 hover:bg-gray-800/50 transition-colors touch-manipulation rounded-lg"
                          >
                            <service.icon className="w-5 h-5 mr-3 text-primary" />
                            <div>
                              <div className="font-medium">{service.name}</div>
                              <p className="text-sm text-gray-400 mt-0.5">
                                {service.description}
                              </p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>

                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      href={item.href}
                      onClick={() => setOpen(false)}
                      className={`${location === item.href
                        ? "text-primary font-semibold bg-primary/10"
                        : "text-gray-300"
                        } px-4 py-3 text-lg font-medium hover:bg-gray-800/50 rounded-lg transition-colors touch-manipulation`}
                    >
                      {t(item.key || item.name.toLowerCase())}
                    </Link>
                  ))}
                  <Link
                    href="/wishlist"
                    onClick={() => setOpen(false)}
                    className="px-4 py-3 text-lg font-medium text-gray-300 hover:bg-gray-800/50 rounded-lg transition-colors touch-manipulation"
                  >
                    <div className="flex items-center">
                      <Heart className="w-5 h-5 mr-3" />
                      {t("wishlist")}
                    </div>
                  </Link>
                  <Link
                    href="/gallery"
                    onClick={() => setOpen(false)}
                    className="px-4 py-3 text-lg font-medium text-gray-300 hover:bg-gray-800/50 rounded-lg transition-colors touch-manipulation"
                  >
                    <div className="flex items-center">
                      <Image className="w-5 h-5 mr-3" />
                      {t("gallery")}
                    </div>
                  </Link>



                  <div className="py-2">
                    <div className="px-4 py-3">
                      <MobileTranslate />
                    </div>
                  </div>
                </div>

                <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent py-6 px-4">
                  <div className="flex justify-center items-center gap-6">
                    <a
                      href="https://facebook.com/Ecoplace-Tours/100093548723777"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-primary transition-colors"
                    >
                      <Facebook className="w-6 h-6" />
                    </a>
                    <a
                      href="https://x.com/Ecoplacetours"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-primary transition-colors"
                    >
                      <FontAwesomeIcon icon={faXTwitter} className="w-6 h-6" />
                    </a>
                    <a
                      href="https://instagram.com/ecoplacetours"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-primary transition-colors"
                    >
                      <Instagram className="w-6 h-6" />
                    </a>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          <div className="hidden md:flex items-center justify-between flex-1 pl-6">
            <div className="flex justify-center items-center space-x-6 mx-auto max-w-4x1">
              <Link
                href="/"
                className={`${location === "/"
                  ? "text-primary font-semibold"
                  : "text-gray-300 hover:text-white"
                  } px-2 py-2 text-sm font-medium transition-colors whitespace-nowrap`}
              >
                {t("home")}
              </Link>

              <div
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className="relative"
              >
                <button className="group px-2 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors focus:outline-none whitespace-nowrap">
                  {t("destinations")}
                </button>
                {dropdownOpen && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full z-50 w-[640px] bg-gradient-to-b from-gray-50 to-white rounded-lg shadow-lg border border-gray-200/50 backdrop-blur-sm animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 duration-200">
                    <div className="flex gap-4 p-4">
                      <div className="w-1/3 border-r border-gray-200/80 pr-4">
                        {countries.map((country) => (
                          <div
                            key={country.name}
                            onMouseEnter={() => setActiveCountry(country)}
                            className={`p-2.5 mb-1.5 rounded-md transition-all duration-200 cursor-pointer ${activeCountry === country
                              ? "bg-green-50 border-l-2 border-green-600"
                              : "hover:bg-gray-50 hover:shadow-sm"
                              }`}
                          >
                            <span className="font-medium text-gray-900">
                              {country.name}
                            </span>
                            <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">
                              {country.description}
                            </p>
                          </div>
                        ))}
                      </div>
                      <div className="w-2/3">
                        <div className="mb-3 pb-2 border-b border-gray-200/80">
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {activeCountry.name}
                            </h3>
                            <span className="px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700 rounded-full">
                              {activeCountry.tours.length} tours
                            </span>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-1">
                          {activeCountry.tours.map((tour) => (
                            <Link
                              key={tour.href}
                              href={tour.href}
                              className="group px-3 py-2 rounded-md hover:bg-gray-50 transition-all duration-200"
                              onClick={() => setDropdownOpen(false)}
                            >
                              <div className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-green-600 rounded-full group-hover:scale-125 transition-transform" />
                                <span className="text-gray-600 group-hover:text-gray-900 transition-colors">
                                  {tour.name}
                                </span>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div
                onMouseEnter={handleServicesMouseEnter}
                onMouseLeave={handleServicesMouseLeave}
                className="relative"
              >
                <button className="group px-2 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors focus:outline-none whitespace-nowrap">
                  {t("services")}
                </button>
                {servicesDropdownOpen && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full z-50 w-[800px] bg-white rounded-lg shadow-lg border animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 duration-200">
                    <div className="flex gap-6 p-6">
                      <div className="w-1/3 border-r pr-4">
                        {services.map((service) => (
                          <div
                            key={service.name}
                            onMouseEnter={() => setActiveService(service)}
                            className="p-2 mb-2 rounded-md hover:bg-gray-100 cursor-pointer"
                          >
                            <div className="flex items-center">
                              <service.icon className="w-5 h-5 mr-2 text-primary" />
                              <span className="font-medium text-gray-900">
                                {service.name}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="w-2/3 p-2">
                        <div className="flex items-center mb-4">
                          <activeService.icon className="w-8 h-8 text-primary mr-3" />
                          <h3 className="text-lg font-semibold">
                            {activeService.name}
                          </h3>
                        </div>
                        <p className="text-gray-600 mb-4">
                          {activeService.description}
                        </p>
                        <div className="space-y-2">
                          {activeService.features.map((feature, index) => (
                            <div
                              key={index}
                              className="flex items-center text-sm text-gray-500"
                            >
                              <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
                              {feature}
                            </div>
                          ))}
                        </div>
                        <Link
                          href={activeService.href}
                          className="mt-4 inline-block text-primary hover:underline"
                        >
                          Learn more about {activeService.name} →
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {navigation.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className={`${location === item.href
                    ? "text-primary font-semibold"
                    : "text-gray-300 hover:text-white"
                    } px-2 py-2 text-sm font-medium transition-colors whitespace-nowrap`}
                >
                  {t(item.key || item.name.toLowerCase())}
                </Link>
              ))}
            </div>

            <div className="flex items-center space-x-4">
              <Link
                href="/wishlist"
                className="flex items-center px-2 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors whitespace-nowrap"
              >
                <Heart className="w-5 h-5 mr-2" />
                {t("wishlist")}
              </Link>
              <Link
                href="/gallery"
                className="flex items-center px-2 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors whitespace-nowrap"
              >
                <Image className="w-5 h-5 mr-2" />
                {t("gallery")}
              </Link>

              {/* DESKTOP LANGUAGE DROPDOWN REPLACED */}
              <div id="google_translate_element" className="hidden" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="text-gray-300 hover:text-white px-2 py-2 flex items-center space-x-2">
                    <span className="text-lg">{currentLangObj.flag}</span>
                    <span>{currentLangObj.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[150px] max-h-[300px] overflow-y-auto bg-white z-[100]">
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code);
                        if (lang.code === 'en') {
                          const domain = window.location.hostname;
                          document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
                          document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=${domain}; path=/;`;
                          document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=.${domain}; path=/;`;
                          window.location.reload();
                          return;
                        }
                        const selectId = '.goog-te-combo';
                        const selects = document.querySelectorAll(selectId);
                        selects.forEach(select => {
                          (select as HTMLSelectElement).value = lang.code;
                          select.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
                        });
                        document.cookie = `googtrans=/en/${lang.code}; path=/`;
                        document.cookie = `googtrans=/en/${lang.code}; domain=.${window.location.hostname}; path=/`;
                        if (!document.querySelector('.goog-te-banner-frame')) {
                          window.location.reload();
                        }
                      }}
                      className="cursor-pointer flex items-center gap-3 py-2"
                    >
                      <span className="text-xl">{lang.flag}</span>
                      <span className="font-medium">{lang.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
